import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin } from 'lucide-react';
import { MapLayer } from '../services/api';
import 'leaflet/dist/leaflet.css';

interface MatchedAsset {
    layer_name: string;
    asset_id?: string;
    asset_type?: string;
    properties?: Record<string, any>;
    distance_meters?: number;
}

interface RequestDetailMapProps {
    lat: number;
    lng: number;
    matchedAsset?: MatchedAsset | null;
    mapLayers: MapLayer[];
    apiKey?: string;
}

const requestIcon = L.divIcon({
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
        <path d="M14 0 C6.268 0 0 6.268 0 14 C0 24.5 14 40 14 40 C14 40 28 24.5 28 14 C28 6.268 21.732 0 14 0 Z"
              fill="#ef4444" filter="drop-shadow(0 2px 3px rgba(0,0,0,0.3))"/>
        <circle cx="14" cy="14" r="5" fill="white"/>
    </svg>`,
    className: '',
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -40],
});

const assetIcon = L.divIcon({
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="34" viewBox="0 0 24 34">
        <path d="M12 0 C5.373 0 0 5.373 0 12 C0 21 12 34 12 34 C12 34 24 21 24 12 C24 5.373 18.627 0 12 0 Z"
              fill="#f59e0b" filter="drop-shadow(0 2px 3px rgba(0,0,0,0.3))"/>
        <circle cx="12" cy="12" r="4" fill="white"/>
    </svg>`,
    className: '',
    iconSize: [24, 34],
    iconAnchor: [12, 34],
    popupAnchor: [0, -34],
});

function PanTo({ lat, lng }: { lat: number; lng: number }) {
    const map = useMap();
    const movedRef = useRef(false);
    useEffect(() => {
        if (!movedRef.current) {
            movedRef.current = true;
            map.setView([lat, lng], 18);
        }
    }, [lat, lng, map]);
    return null;
}

function InvalidateSize() {
    const map = useMap();
    useEffect(() => {
        const t1 = setTimeout(() => map.invalidateSize(), 50);
        const t2 = setTimeout(() => map.invalidateSize(), 300);
        const t3 = setTimeout(() => map.invalidateSize(), 800);
        const onOrientation = () => setTimeout(() => map.invalidateSize(), 300);
        window.addEventListener('orientationchange', onOrientation);
        return () => {
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
            window.removeEventListener('orientationchange', onOrientation);
        };
    }, [map]);
    return null;
}

export default function RequestDetailMap({ lat, lng, matchedAsset, mapLayers }: RequestDetailMapProps) {
    const layerStyle: L.PathOptions = { color: '#6366f1', weight: 2, fillColor: '#6366f1', fillOpacity: 0.2 };

    return (
        <div className="w-full h-full min-h-[200px]">
            <MapContainer
                center={[lat, lng]}
                zoom={18}
                className="w-full h-full"
                style={{ background: '#1a1a2e', minHeight: '200px' }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <InvalidateSize />
                <PanTo lat={lat} lng={lng} />

                {/* GeoJSON layers */}
                {mapLayers.map((layer, i) => {
                    if (!layer.geojson) return null;
                    return (
                        <GeoJSON key={`layer-${i}`} data={layer.geojson as any}
                            style={() => ({ color: layer.fill_color || '#6366f1', weight: 2, fillOpacity: 0.2 })} />
                    );
                })}

                {/* Matched asset marker */}
                {matchedAsset && (
                    <Marker position={[lat, lng]} icon={assetIcon}>
                        <Popup>
                            <div className="p-1">
                                <b className="text-sm">{matchedAsset.layer_name}</b>
                                {matchedAsset.asset_id && <p className="text-xs text-gray-500">ID: {matchedAsset.asset_id}</p>}
                                {matchedAsset.asset_type && <p className="text-xs text-gray-500">Type: {matchedAsset.asset_type}</p>}
                                {matchedAsset.distance_meters != null && (
                                    <p className="text-xs text-gray-500">{matchedAsset.distance_meters.toFixed(1)}m away</p>
                                )}
                            </div>
                        </Popup>
                    </Marker>
                )}

                {/* Request location marker */}
                <Marker position={[lat, lng]} icon={requestIcon}>
                    <Popup>
                        <div className="p-1 flex items-center gap-1">
                            <MapPin className="w-4 h-4 text-red-500" />
                            <span className="text-sm font-medium">Request location</span>
                        </div>
                    </Popup>
                </Marker>
            </MapContainer>
        </div>
    );
}
